import React, { Component, createContext } from 'react';

//* HOCs
import { withRouter } from 'next/router';

//* Helpers
import { ThemeProvider } from 'styled-components';
import themes from 'styles/theme';
import HelperClass from 'styles/helperClass';
import Variables from 'styles/variables';
import Typography from 'styles/typography';
import { routerToPathAndQuery } from 'helpers/_routerToPathAndQuery';

//* Global Variables
const errorsInitialState = {
	formError: {
		error: false,
		names: [],
		msg: {},
	},
	systemError: {
		error: false,
		msg: '',
	},
};

const UIContext = createContext(null);

class UIProvider extends Component {
	//! States
	state = {
		winWidth: 1920,
		winHeight: 1080,
		screenSizes: {},
		...errorsInitialState,
		preloaderHide: false,
		preloader: true,
		isLandingPage: false,
		popupIsOpen: false,
		popupComponent: null,
		popupProps: {},
	};

	//! Methods
	methods = {
		openPopup: this.openPopup.bind(this),
		closePopup: this.closePopup.bind(this),
		togglePopup: this.togglePopup.bind(this),
		disableDomScroll: this.disableDomScroll.bind(this),
		enableDomScroll: this.enableDomScroll.bind(this),
	};

	//! Screen Resize
	screenResize = () => {
		this.setState({
			winWidth: window.innerWidth,
			winHeight: window.innerHeight,
		});
	};

	//! Get Media Screen Sizes from Css Variables
	getScreenSizes() {
		const root = getComputedStyle(document.documentElement);

		const screenSizes = {
			desktopSizeXL: parseInt(root.getPropertyValue('--desktopSizeXL')),
			desktopSizeL: parseInt(root.getPropertyValue('--desktopSizeL')),
			desktopSizeM: parseInt(root.getPropertyValue('--desktopSizeM')),
			desktopSizeS: parseInt(root.getPropertyValue('--desktopSizeS')),
			desktopSizeXS: parseInt(root.getPropertyValue('--desktopSizeXS')),
			tabletSize: parseInt(root.getPropertyValue('--tabletSize')),
			tabletSizeS: parseInt(root.getPropertyValue('--tabletSizeS')),
			mobileSize: parseInt(root.getPropertyValue('--mobileSize')),
		};

		this.setState({ screenSizes });
	}

	//! Open Popup
	openPopup(popupComponent = null, popupProps = {}) {
		if (typeof popupComponent === 'string' && this.popup.includes(popupComponent) && this.props.router.query?.c !== popupComponent) {
			const { pathname, query } = routerToPathAndQuery(this.props.router, 'c', popupComponent);
			this.props.router.push({ pathname, query }, undefined, { shallow: true });
		}

		this.setState({
			popupIsOpen: true,
			popupComponent,
			popupProps,
		});
	}

	//! Close Popup
	closePopup(scroll = false) {
		const { pathname, query } = routerToPathAndQuery(this.props.router, 'c');
		this.props.router.push({ pathname, query }, undefined, { scroll: false });

		this.setState({
			popupIsOpen: false,
			popupComponent: null,
		});
	}

	//! Toggle Popup
	togglePopup() {
		this.setState({
			popupIsOpen: !this.state.popupIsOpen,
		});
	}

	//! Disable DOM Scroll
	disableDomScroll() {
		// document.querySelector('html').classList.add('hide-scroll')

		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

		// if any scroll is attempted, set this to the previous value
		window.onscroll = function (e) {
			e.preventDefault();
			window.scrollTo(scrollLeft, scrollTop);
		};
	}

	//! Enable DOM Scroll
	enableDomScroll() {
		// document.querySelector('html').classList.remove('hide-scroll');

		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

		window.onscroll = function () {};
		window.scrollTo(scrollLeft, scrollTop);
	}

	//! Component Did Mount
	componentDidMount() {
		this.setState({
			...this.state,
			isLandingPage: this.props.router.asPath.includes('landing'),
		});

		window.addEventListener('resize', this.screenResize);
		this.getScreenSizes();
		this.screenResize();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.router.asPath !== this.props.router.asPath) {
			this.setState({
				...this.state,
				isLandingPage: this.props.router.asPath.includes('landing'),
			});
		}
	}

	//! Component Will Unmount
	componentWillUnmount() {
		window.addEventListener('resize', this.screenResize);
		document.removeEventListener('keydown', this.escKeydown, false);
	}

	render() {
		return (
			<UIContext.Provider value={{ ...this.state, ...this.methods }}>
				<ThemeProvider theme={themes}>
					<Variables />
					<Typography popupIsOpen={this.state.popupIsOpen} />
					<HelperClass />

					{this.props.children}
				</ThemeProvider>
			</UIContext.Provider>
		);
	}
}

export default withRouter(UIProvider);
export const UIConsumer = UIContext.Consumer;
